import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, ShieldX, Eye, Repeat, Loader2, Link2Off } from "lucide-react";
import { connectBinance, disconnectBinance } from "@/lib/api";
import { toast } from "sonner";

const Perm = ({ ok, icon: Icon, label }) => (
  <div className="flex items-center gap-2.5 text-sm">
    <div className={`h-6 w-6 rounded flex items-center justify-center ${ok ? "bg-[#00FFA3]/15 text-[var(--pos)]" : "bg-[#FF3366]/15 text-[var(--neg)]"}`}>
      <Icon className="h-3.5 w-3.5" />
    </div>
    <span className="text-[var(--text-muted)]">{label}</span>
    <span className={`ml-auto text-xs font-mono ${ok ? "text-[var(--pos)]" : "text-[var(--neg)]"}`}>
      {ok ? "AÇIK" : "KAPALI"}
    </span>
  </div>
);

export const ConnectBinanceDialog = ({ open, onOpenChange, status, onChanged }) => {
  const [apiKey, setApiKey] = useState("");
  const [apiSecret, setApiSecret] = useState("");
  const [busy, setBusy] = useState(false);
  const connected = status?.connected;

  const submit = async () => {
    if (apiKey.length < 8 || apiSecret.length < 8) {
      toast.error("Lütfen geçerli API anahtarı ve secret girin.");
      return;
    }
    setBusy(true);
    try {
      await connectBinance({ api_key: apiKey, api_secret: apiSecret });
      toast.success("Binance bağlantısı kuruldu (okuma + spot işlem).");
      setApiKey(""); setApiSecret("");
      onChanged();
    } catch (e) {
      toast.error("Bağlantı başarısız: " + (e.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const disconnect = async () => {
    setBusy(true);
    try { await disconnectBinance(); toast.success("Binance bağlantısı kaldırıldı."); onChanged(); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="binance-dialog" className="bg-[var(--surface)] border-[var(--border)] text-[var(--text-primary)] max-w-md">
        <DialogHeader>
          <DialogTitle className="font-head text-xl">Binance Bağlantısı</DialogTitle>
          <DialogDescription className="text-[var(--text-dim)] text-sm">
            Sadece veri okuma ve spot alım-satım yetkisi kullanılır. Para çekme yetkisi kesinlikle kapalıdır.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 rounded-lg border border-[var(--border)] bg-[var(--app-bg)] p-4">
          <Perm ok icon={Eye} label="Veri Okuma" />
          <Perm ok icon={Repeat} label="Spot Alım-Satım" />
          <Perm ok={false} icon={ShieldX} label="Para Çekme (Withdraw)" />
        </div>

        {connected ? (
          <div className="space-y-4">
            <div className="flex items-center gap-2 rounded-lg bg-[#00FFA3]/10 border border-[#00FFA3]/25 px-4 py-3">
              <ShieldCheck className="h-5 w-5 text-[var(--pos)]" />
              <div>
                <div className="text-sm font-bold text-[var(--pos)]">Bağlı</div>
                <div className="text-xs text-[var(--text-dim)] font-mono">{status.api_key_masked}</div>
              </div>
            </div>
            <Button data-testid="disconnect-binance-submit" onClick={disconnect} disabled={busy}
              variant="outline" className="w-full h-11 border-[#FF3366]/40 text-[var(--neg)] hover:bg-[#FF3366]/10 hover:text-[var(--neg)]">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Link2Off className="h-4 w-4 mr-1.5" /> Bağlantıyı Kaldır</>}
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div>
              <Label className="text-xs text-[var(--text-dim)]">API Key</Label>
              <Input data-testid="binance-api-key" value={apiKey} onChange={(e) => setApiKey(e.target.value)}
                placeholder="API anahtarınız" className="mt-1 bg-[var(--app-bg)] border-[var(--border)] font-mono text-sm" />
            </div>
            <div>
              <Label className="text-xs text-[var(--text-dim)]">API Secret</Label>
              <Input data-testid="binance-api-secret" type="password" value={apiSecret} onChange={(e) => setApiSecret(e.target.value)}
                placeholder="Secret anahtarınız" className="mt-1 bg-[var(--app-bg)] border-[var(--border)] font-mono text-sm" />
            </div>
            <Button data-testid="binance-connect-submit" onClick={submit} disabled={busy}
              className="w-full h-12 bg-[var(--pos)] text-black font-bold hover:bg-[var(--pos-hover)] hover:text-black">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Güvenli Bağlan"}
            </Button>
            <p className="text-[11px] text-[var(--text-faint)] leading-relaxed">
              Not: İşlemler bu ortamda kağıt (paper) trading olarak simüle edilir; gerçek fon riski yoktur.
            </p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
